import { z } from "zod";

export const HOME_EXECUTIVE_METRIC_DICTIONARY_VERSION = 1;
export const HOME_EXECUTIVE_CARD_DICTIONARY = Object.freeze({
  "vendor.overview": Object.freeze({
    pageKey: "vendorManagementVendors", route: "/vendor-management/vendors",
    metricKeys: Object.freeze(["critical", "high", "active", "inactive"])
  }),
  "incident.operations": Object.freeze({
    pageKey: "incidentManagementIncidents", route: "/incident-management/incidents",
    metricKeys: Object.freeze(["open", "criticalHigh", "slaBreached"])
  }),
  "bia.readiness": Object.freeze({
    pageKey: "bcmBia", route: "/bcm/bia",
    metricKeys: Object.freeze(["draftReview", "approved", "highImpact", "recoveryWarnings"])
  }),
  "bcp.review": Object.freeze({
    pageKey: "bcmBcp", route: "/bcm/bcp",
    metricKeys: Object.freeze(["reviewDue", "draftReview", "approved"])
  }),
  "exercise.schedule": Object.freeze({
    pageKey: "bcmExercises", route: "/bcm/exercises",
    metricKeys: Object.freeze(["overdue", "dueNext30Days", "plannedInProgress"])
  }),
  "gdpr.dsr.deadlines": Object.freeze({
    pageKey: "gdprDataSubjectRequest", route: "/gdpr-operations/data-subject-request",
    metricKeys: Object.freeze(["overdue", "dueNext30Days", "open"])
  }),
  "gdpr.breach.notifications": Object.freeze({
    pageKey: "gdprDataBreach", route: "/gdpr-operations/data-breach",
    metricKeys: Object.freeze(["authorityPending", "subjectPending", "open"])
  }),
  "ticket.workload": Object.freeze({
    pageKey: "ticketing", route: "/ticketing",
    metricKeys: Object.freeze(["open", "slaBreached"])
  })
});

const HomeExecutiveMetricSchema = z.object({
  key: z.string().trim().min(1).max(80),
  value: z.number().int().nonnegative()
}).strict();

export const HomeExecutiveCardSchema = z.object({
  key: z.enum(Object.keys(HOME_EXECUTIVE_CARD_DICTIONARY)),
  route: z.string().trim().min(1).max(200),
  metrics: z.array(HomeExecutiveMetricSchema).min(1).max(20)
}).strict().superRefine((card, ctx) => {
  const expected = HOME_EXECUTIVE_CARD_DICTIONARY[card.key];
  if (card.route !== expected.route) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["route"], message: "Executive card route does not match the stable dictionary" });
  }
  const keys = card.metrics.map((metric) => metric.key);
  if (new Set(keys).size !== keys.length || keys.join("|") !== expected.metricKeys.join("|")) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["metrics"], message: "Executive card metrics do not match the stable dictionary" });
  }
});

export const HomeExecutiveSummarySchema = z.object({
  metricDictionaryVersion: z.literal(HOME_EXECUTIVE_METRIC_DICTIONARY_VERSION),
  asOfUtc: z.string().datetime({ offset: true }),
  cards: z.array(HomeExecutiveCardSchema).max(Object.keys(HOME_EXECUTIVE_CARD_DICTIONARY).length)
    .refine((cards) => new Set(cards.map((card) => card.key)).size === cards.length, { message: "Executive cards must be unique" })
}).strict();

/** SCF internal-control list: tenant scope preference + tenant_overview_status distribution. */
export const HomeInternalControlScopeSummarySchema = z.object({
  total: z.number().nonnegative(),
  notStarted: z.number().nonnegative(),
  inProgress: z.number().nonnegative(),
  complete: z.number().nonnegative(),
  completePercent: z.number().min(0).max(100)
});

export const HomeMetricsSchema = z.object({
  totalAssets: z.number().nonnegative(),
  activeUsers: z.number().nonnegative(),
  internalControlScopeSummary: HomeInternalControlScopeSummarySchema,
  registeredRisks: z.number().nonnegative(),
  pendingDocuments: z.number().nonnegative()
});

/** Counts per envanter UI grubu (AssetInfoCard / ilişki grafiği ile uyumlu). */
export const HomeAssetGroupDistributionSchema = z.object({
  businessAssets: z.number().nonnegative(),
  informationAssets: z.number().nonnegative(),
  applicationAssets: z.number().nonnegative(),
  infrastructureAssets: z.number().nonnegative(),
  locationAssets: z.number().nonnegative(),
  humanAssets: z.number().nonnegative(),
  thirdPartyAssets: z.number().nonnegative(),
  unmapped: z.number().nonnegative()
});

export const HomeTicketPriorityBucketSchema = z.object({
  _id: z.unknown().nullable(),
  count: z.number().nonnegative()
});

export const HomeTicketSummarySchema = z.object({
  total: z.number().nonnegative(),
  draft: z.number().nonnegative().optional(),
  returned: z.number().nonnegative().optional(),
  open: z.number().nonnegative(),
  inApproval: z.number().nonnegative(),
  assigned: z.number().nonnegative(),
  resolved: z.number().nonnegative(),
  breached: z.number().nonnegative(),
  poolCount: z.number().nonnegative(),
  byPriority: z.array(HomeTicketPriorityBucketSchema),
  byCategory: z.array(HomeTicketPriorityBucketSchema).optional()
});

export const HomePageDataSchema = z.object({
  metrics: HomeMetricsSchema,
  assetGroupDistribution: HomeAssetGroupDistributionSchema,
  ticketSummary: HomeTicketSummarySchema.optional(),
  executiveSummary: HomeExecutiveSummarySchema.optional()
});

export const HomePageResponseSchema = z.object({
  success: z.boolean(),
  data: HomePageDataSchema,
  message: z.string().optional()
});

export const DrillDownResponseSchema = z.object({
  success: z.boolean(),
  data: z.unknown(),
  message: z.string().optional(),
}).passthrough();
